import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

const TYPES = ["Hotel", "Restaurant", "Merchant", "Dairy Shop", "Retailer", "Wholesaler"];
const CYCLES = ["Daily", "Weekly", "Monthly"];

const EMPTY = {
  customer_name: "", business_name: "", business_type: "Hotel",
  mobile: "", mobile_secondary: "", email: "", address: "", gst_number: "", credit_limit: 0,
  payment_cycle: "Monthly", status: true,
  portal_username: "", portal_password: "",
};

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [cycleFilter, setCycleFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("business_name_asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/customers").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  // Filter logic
  let processed = list.filter(c => {
    const matchesQ = !q ||
      c.business_name.toLowerCase().includes(q.toLowerCase()) ||
      c.customer_name.toLowerCase().includes(q.toLowerCase()) ||
      (c.mobile || "").includes(q) ||
      (c.mobile_secondary || "").includes(q);
    const matchesType = typeFilter === "ALL" || c.business_type === typeFilter;
    const matchesCycle = cycleFilter === "ALL" || c.payment_cycle === cycleFilter;
    return matchesQ && matchesType && matchesCycle;
  });

  // Sort logic
  processed.sort((a, b) => {
    if (sortBy === "business_name_asc") return a.business_name.localeCompare(b.business_name);
    if (sortBy === "business_name_desc") return b.business_name.localeCompare(a.business_name);
    if (sortBy === "outstanding_desc") return (b.outstanding || 0) - (a.outstanding || 0);
    if (sortBy === "outstanding_asc") return (a.outstanding || 0) - (b.outstanding || 0);
    if (sortBy === "advance_desc") return (b.advance || 0) - (a.advance || 0);
    return 0;
  });

  // Pagination logic
  const totalItems = processed.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedList = processed.slice(startIdx, startIdx + pageSize);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [q, typeFilter, cycleFilter, sortBy, pageSize]);

  function openCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(c) {
    setEditing(c);
    setForm({ ...EMPTY, ...c, portal_username: "", portal_password: "" });
    setOpen(true);
  }
  async function save() {
    try {
      const payload = { ...form, credit_limit: Number(form.credit_limit) };
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Customer updated" : "Customer created");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  async function remove(c) {
    if (!window.confirm(`Delete ${c.business_name}? This deletes their pricing too.`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  return (
    <div data-testid="customers-page">
      <PageHeader
        eyebrow="Network"
        title="Customers"
        description="Hotels, restaurants, dairy shops, retailers, and wholesalers."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="customer-create-btn"><Plus size={16} className="mr-1"/>New Customer</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Contact Person</Label><Input data-testid="customer-name" value={form.customer_name} onChange={e=>setForm({...form, customer_name: e.target.value})}/></div>
                <div><Label>Business Name</Label><Input data-testid="customer-business" value={form.business_name} onChange={e=>setForm({...form, business_name: e.target.value})}/></div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.business_type} onValueChange={(v)=>setForm({...form, business_type: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Cycle</Label>
                  <Select value={form.payment_cycle} onValueChange={(v)=>setForm({...form, payment_cycle: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{CYCLES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Primary Mobile (Owner)</Label><Input placeholder="e.g. +91 9876543210" value={form.mobile} onChange={e=>setForm({...form, mobile: e.target.value})}/></div>
                <div><Label>Secondary Mobile (Cashier - Optional)</Label><Input placeholder="e.g. +91 9123456789" value={form.mobile_secondary} onChange={e=>setForm({...form, mobile_secondary: e.target.value})}/></div>
                <div><Label>Email</Label><Input value={form.email} onChange={e=>setForm({...form, email: e.target.value})}/></div>
                <div><Label>GST Number</Label><Input value={form.gst_number} onChange={e=>setForm({...form, gst_number: e.target.value})}/></div>
                <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit} onChange={e=>setForm({...form, credit_limit: e.target.value})}/></div>
                <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={e=>setForm({...form, address: e.target.value})}/></div>
                <div className="col-span-2 border-t border-slate-200 pt-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Portal Access (optional)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Username</Label><Input placeholder={editing ? "(unchanged)" : "e.g. hotelxyz"} value={form.portal_username} onChange={e=>setForm({...form, portal_username: e.target.value})}/></div>
                    <div><Label>Password</Label><Input type="text" placeholder={editing ? "(unchanged)" : "Set password"} value={form.portal_password} onChange={e=>setForm({...form, portal_password: e.target.value})}/></div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-cobalt-500 hover:bg-cobalt-700" data-testid="customer-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card padding="p-0">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          {/* Search bar */}
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-3 top-3 text-slate-400"/>
            <Input data-testid="customer-search" placeholder="Search by name, business, mobile…" className="pl-9" value={q} onChange={e=>setQ(e.target.value)}/>
          </div>

          {/* Filter & Sort Controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Business Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 h-9 text-xs">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Types</SelectItem>
                {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Payment Cycle Filter */}
            <Select value={cycleFilter} onValueChange={setCycleFilter}>
              <SelectTrigger className="w-32 h-9 text-xs">
                <SelectValue placeholder="All Cycles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Cycles</SelectItem>
                {CYCLES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Sort Options */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-44 h-9 text-xs">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="business_name_asc">Name (A to Z)</SelectItem>
                <SelectItem value="business_name_desc">Name (Z to A)</SelectItem>
                <SelectItem value="outstanding_desc">Outstanding (High to Low)</SelectItem>
                <SelectItem value="outstanding_asc">Outstanding (Low to High)</SelectItem>
                <SelectItem value="advance_desc">Advance (High to Low)</SelectItem>
              </SelectContent>
            </Select>

            {/* Page Size */}
            <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
              <SelectTrigger className="w-28 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="15">15 per page</SelectItem>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {paginatedList.length === 0 ? <div className="p-8"><Empty title="No customers found"/></div> : (
          <>
            <div className="overflow-x-auto">
              <table className="ddms-table" data-testid="customers-table">
                <thead>
                  <tr><th>Business</th><th>Contact</th><th>Type</th><th>Mobile Numbers</th><th>Cycle</th><th className="num">Outstanding</th><th className="num">Advance</th><th className="num">Actions</th></tr>
                </thead>
                <tbody>
                  {paginatedList.map(c => (
                    <tr key={c.id}>
                      <td className="font-medium text-slate-900">{c.business_name}</td>
                      <td>{c.customer_name}</td>
                      <td><span className="px-2 py-0.5 rounded-full text-[11px] bg-cobalt-50 text-cobalt-700 font-medium">{c.business_type}</span></td>
                      <td className="font-mono text-xs">
                        <div><span className="text-slate-500 text-[10px] uppercase font-sans">Owner:</span> {c.mobile || "—"}</div>
                        {c.mobile_secondary && (
                          <div className="text-slate-600 mt-0.5"><span className="text-slate-400 text-[10px] uppercase font-sans">Cashier:</span> {c.mobile_secondary}</div>
                        )}
                      </td>
                      <td>{c.payment_cycle}</td>
                      <td className="num text-rose-600">{(c.outstanding||0).toFixed(2)}</td>
                      <td className="num text-emerald-600">{(c.advance||0).toFixed(2)}</td>
                      <td className="num">
                        <Link to={`/ledger/${c.id}`}><Button size="sm" variant="outline" className="mr-2"><BookOpen size={12}/></Button></Link>
                        <Button size="sm" variant="outline" onClick={()=>openEdit(c)} className="mr-2"><Pencil size={12}/></Button>
                        <Button size="sm" variant="outline" onClick={()=>remove(c)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={12}/></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls Footer */}
            <div className="p-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
              <div>
                Showing <span className="font-semibold text-slate-900">{startIdx + 1}</span> to <span className="font-semibold text-slate-900">{Math.min(startIdx + pageSize, totalItems)}</span> of <span className="font-semibold text-slate-900">{totalItems}</span> customers
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setPage(p => Math.max(p - 1, 1))}
                  className="h-8 px-3 text-xs"
                >
                  Previous
                </Button>
                <div className="px-3 py-1 font-medium text-slate-800">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                  className="h-8 px-3 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
